import type {
  RetiredCodeBehaviour,
  ScanAnalyticsTopIdentifierItem,
  SectorId,
  WorkspaceDocument,
  WorkspaceQrStyleConfig,
} from '@myplayer/schema';
import {
  getFirebaseClients,
  isFunctionsEmulatorOrLiveBackendConfigured,
  isLocalEmulatorMode,
  loadFirebaseWebConfig,
} from './firebase.js';
import { httpsCallable } from 'firebase/functions';

async function invokeCallable<Req, Res>(name: string, payload: Req): Promise<Res> {
  const { functions } = getFirebaseClients();
  const res = await httpsCallable<Req, Res>(functions, name)(payload);
  return res.data;
}

export interface GetWorkspaceScanAnalyticsCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly fromDay?: string;
  readonly toDay?: string;
  readonly identifierKey?: string;
}

export interface GetWorkspaceScanAnalyticsCallableResult {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly fromDay?: string;
  readonly toDay?: string;
  readonly totalScans: number;
  readonly cacheHits: number;
  readonly cacheMisses: number;
  readonly byIdentifier: Readonly<Record<string, number>>;
  readonly byDay: Readonly<Record<string, number>>;
  readonly byCountry: Readonly<Record<string, number>>;
  readonly byDeviceCategory?: Readonly<Record<string, number>>;
  readonly topIdentifiers?: readonly ScanAnalyticsTopIdentifierItem[];
}

export async function callGetWorkspaceScanAnalyticsFunction(
  payload: GetWorkspaceScanAnalyticsCallablePayload,
): Promise<GetWorkspaceScanAnalyticsCallableResult> {
  if (!import.meta.env.PROD && isLocalEmulatorMode()) {
    const emptyResult: GetWorkspaceScanAnalyticsCallableResult = {
      orgId: payload.orgId,
      workspaceId: payload.workspaceId,
      ...(payload.fromDay !== undefined ? { fromDay: payload.fromDay } : {}),
      ...(payload.toDay !== undefined ? { toDay: payload.toDay } : {}),
      totalScans: 0,
      cacheHits: 0,
      cacheMisses: 0,
      byIdentifier: {},
      byDay: {},
      byCountry: {},
      byDeviceCategory: {},
      topIdentifiers: [],
    };
    if (!isFunctionsEmulatorOrLiveBackendConfigured()) return emptyResult;
    const { auth, functions } = getFirebaseClients();
    const fn = httpsCallable<
      GetWorkspaceScanAnalyticsCallablePayload,
      GetWorkspaceScanAnalyticsCallableResult
    >(functions, 'getWorkspaceScanAnalytics');
    try {
      return (await fn(payload)).data;
    } catch (err: unknown) {
      if (loadFirebaseWebConfig().functionsEmulatorHost !== undefined) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/revoked|expired|token/i.test(msg) && auth.currentUser) {
          try {
            await auth.currentUser.getIdToken(true);
            return (await fn(payload)).data;
          } catch {
            return emptyResult;
          }
        }
        if (/Unknown callable|NOT_FOUND|revoked/i.test(msg)) return emptyResult;
      }
      throw err;
    }
  }
  return invokeCallable<
    GetWorkspaceScanAnalyticsCallablePayload,
    GetWorkspaceScanAnalyticsCallableResult
  >('getWorkspaceScanAnalytics', payload);
}

export interface SetScheduledChangeCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly targetUrl?: string;
  readonly targetHref?: string;
  readonly validFrom?: string;
  readonly scheduledAt?: string;
  readonly validUntil?: string;
  readonly linkType?: string;
  readonly ruleId?: string;
  readonly priority?: number;
  readonly reason?: string;
}

export interface SetScheduledChangeCallableResult {
  readonly identifierKey: string;
  readonly version: number;
  readonly ruleId: string;
  readonly targetHref: string;
  readonly validFrom?: string;
  readonly validUntil?: string;
  readonly scheduledAt: string;
  readonly auditId: string;
}

export interface CancelScheduledChangeCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly ruleId?: string;
  readonly reason?: string;
}

export interface CancelScheduledChangeCallableResult {
  readonly identifierKey: string;
  readonly version: number;
  readonly cancelledRuleIds: readonly string[];
  readonly cancelledAt: string;
  readonly auditId: string;
}

export async function callSetScheduledChangeFunction(
  payload: SetScheduledChangeCallablePayload,
): Promise<SetScheduledChangeCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    const nowIso = new Date().toISOString();
    const targetHref = (payload.targetHref ?? payload.targetUrl ?? '').trim();
    const ruleId = payload.ruleId?.trim() || 'schedule-1';
    const validFrom = payload.validFrom ?? payload.scheduledAt;
    return {
      identifierKey: payload.identifierKey,
      version: 2,
      ruleId,
      targetHref,
      ...(validFrom !== undefined ? { validFrom } : {}),
      ...(payload.validUntil !== undefined ? { validUntil: payload.validUntil } : {}),
      scheduledAt: nowIso,
      auditId: `aud_${payload.identifierKey}_v2`,
    };
  }
  return invokeCallable<SetScheduledChangeCallablePayload, SetScheduledChangeCallableResult>(
    'setScheduledChange',
    payload,
  );
}

export async function callCancelScheduledChangeFunction(
  payload: CancelScheduledChangeCallablePayload,
): Promise<CancelScheduledChangeCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    const nowIso = new Date().toISOString();
    return {
      identifierKey: payload.identifierKey,
      version: 3,
      cancelledRuleIds: payload.ruleId ? [payload.ruleId] : ['schedule-1'],
      cancelledAt: nowIso,
      auditId: `aud_${payload.identifierKey}_v3`,
    };
  }
  return invokeCallable<CancelScheduledChangeCallablePayload, CancelScheduledChangeCallableResult>(
    'cancelScheduledChange',
    payload,
  );
}

export interface CreateWorkspaceCallablePayload {
  readonly orgId: string;
  readonly workspaceId?: string;
  readonly name: string;
  readonly slug?: string;
  readonly workspaceType?: 'BRAND' | 'RETAILER';
  readonly sector?: SectorId;
  readonly enabledSectors?: readonly SectorId[];
  readonly defaultLocale?: string;
  readonly defaultFallbackUrl?: string;
  readonly retiredCodeBehaviour?: RetiredCodeBehaviour;
  readonly gs1CompanyPrefixes?: readonly string[];
  readonly enforcePrefixValidation?: boolean;
  readonly qrStyle?: WorkspaceQrStyleConfig;
  readonly brandQrStyles?: Readonly<Record<string, WorkspaceQrStyleConfig>>;
  readonly reason?: string;
}

export interface CreateWorkspaceCallableResult extends WorkspaceDocument {
  readonly auditId: string;
}

export interface UpdateWorkspaceCallableFields {
  readonly name?: string;
  readonly slug?: string;
  readonly workspaceType?: 'BRAND' | 'RETAILER';
  readonly sector?: SectorId;
  readonly enabledSectors?: readonly SectorId[];
  readonly defaultLocale?: string;
  readonly defaultFallbackUrl?: string;
  readonly retiredCodeBehaviour?: RetiredCodeBehaviour;
  readonly gs1CompanyPrefixes?: readonly string[];
  readonly enforcePrefixValidation?: boolean;
  readonly qrStyle?: WorkspaceQrStyleConfig;
  readonly brandQrStyles?: Readonly<Record<string, WorkspaceQrStyleConfig>>;
}

export interface UpdateWorkspaceCallablePayload extends UpdateWorkspaceCallableFields {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly patch?: UpdateWorkspaceCallableFields;
  readonly updates?: UpdateWorkspaceCallableFields;
  readonly reason?: string;
}

export interface UpdateWorkspaceCallableResult {
  readonly workspaceId: string;
  readonly orgId: string;
  readonly workspace: WorkspaceDocument;
  readonly updatedAt: string;
  readonly auditId: string;
}

export interface ArchiveWorkspaceCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly reason?: string;
}

export interface ArchiveWorkspaceCallableResult {
  readonly workspaceId: string;
  readonly orgId: string;
  readonly status: 'ARCHIVED';
  readonly updatedAt: string;
  readonly auditId: string;
}

export function callCreateWorkspaceFunction(
  payload: CreateWorkspaceCallablePayload,
): Promise<CreateWorkspaceCallableResult> {
  return invokeCallable<CreateWorkspaceCallablePayload, CreateWorkspaceCallableResult>(
    'createWorkspace',
    payload,
  );
}

export async function callUpdateWorkspaceFunction(
  payload: UpdateWorkspaceCallablePayload,
): Promise<UpdateWorkspaceCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    const nowIso = new Date().toISOString();
    const workspace: WorkspaceDocument = {
      workspaceId: payload.workspaceId,
      orgId: payload.orgId,
      name: payload.name ?? payload.workspaceId,
      slug: payload.slug ?? payload.workspaceId,
      sector: payload.sector ?? 'RETAIL_CPG',
      enabledSectors: payload.enabledSectors ?? [payload.sector ?? 'RETAIL_CPG'],
      status: 'ACTIVE',
      defaultLocale: payload.defaultLocale ?? 'en-GB',
      defaultFallbackUrl: payload.defaultFallbackUrl ?? 'https://myplayr.cloud/fallback',
      ...(payload.retiredCodeBehaviour !== undefined
        ? { retiredCodeBehaviour: payload.retiredCodeBehaviour }
        : {}),
      ...(payload.gs1CompanyPrefixes !== undefined
        ? { gs1CompanyPrefixes: payload.gs1CompanyPrefixes }
        : {}),
      ...(payload.enforcePrefixValidation !== undefined
        ? { enforcePrefixValidation: payload.enforcePrefixValidation }
        : {}),
      ...(payload.qrStyle !== undefined ? { qrStyle: payload.qrStyle } : {}),
      ...(payload.brandQrStyles !== undefined ? { brandQrStyles: payload.brandQrStyles } : {}),
      createdAt: nowIso,
      updatedAt: nowIso,
      updatedBy: 'local_emulator',
    };
    return {
      workspaceId: payload.workspaceId,
      orgId: payload.orgId,
      workspace,
      updatedAt: nowIso,
      auditId: `aud_ws_${payload.workspaceId}`,
    };
  }
  return invokeCallable('updateWorkspace', payload);
}

export async function callArchiveWorkspaceFunction(
  payload: ArchiveWorkspaceCallablePayload,
): Promise<ArchiveWorkspaceCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    const nowIso = new Date().toISOString();
    return {
      workspaceId: payload.workspaceId,
      orgId: payload.orgId,
      status: 'ARCHIVED',
      updatedAt: nowIso,
      auditId: `aud_ws_archive_${payload.workspaceId}`,
    };
  }
  return invokeCallable('archiveWorkspace', payload);
}

export interface OffboardCustomDomainCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly hostname: string;
}

export interface OffboardCustomDomainCallableResult {
  readonly hostname: string;
  readonly status: 'DISABLED';
  readonly removed?: boolean;
  readonly certRemoved?: boolean;
  readonly mapEntryRemoved?: boolean;
  readonly dnsAuthRemoved?: boolean;
  readonly recordRemoved?: boolean;
  readonly offboardedAt: string;
  readonly auditId?: string;
}

export async function callOffboardCustomDomainFunction(
  payload: OffboardCustomDomainCallablePayload,
): Promise<OffboardCustomDomainCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      hostname: payload.hostname,
      status: 'DISABLED',
      offboardedAt: new Date().toISOString(),
    };
  }
  return invokeCallable<OffboardCustomDomainCallablePayload, OffboardCustomDomainCallableResult>(
    'offboardCustomDomain',
    payload,
  );
}

export interface RecheckCustomDomainCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly hostname: string;
}

export interface RecheckCustomDomainCallableResult {
  readonly hostname: string;
  readonly status:
    | 'PENDING_DNS'
    | 'PENDING_TXT'
    | 'VERIFIED'
    | 'PROVISIONING_TLS'
    | 'ACTIVE'
    | 'FAILING'
    | 'FAILED';
  readonly plainStatus?: 'Pending' | 'Verified' | 'Failed';
  readonly tlsStatus: 'PENDING' | 'ACTIVE' | 'RENEWING' | 'FAILED';
  readonly verifiedAt: string | null;
  readonly cnameTarget?: string;
  readonly txtHost?: string;
  readonly txtValue?: string;
  readonly verificationToken?: string;
  readonly acmeChallengeRecord?: {
    readonly name: string;
    readonly data: string;
  } | null;
  readonly failureReason?: string | null;
}

export async function callRecheckCustomDomainFunction(
  payload: RecheckCustomDomainCallablePayload,
): Promise<RecheckCustomDomainCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      hostname: payload.hostname,
      status: 'ACTIVE',
      plainStatus: 'Verified',
      tlsStatus: 'ACTIVE',
      verifiedAt: new Date().toISOString(),
      cnameTarget: 'myplayr.cloud',
      txtHost: `_myplayer-verify.${payload.hostname}`,
      txtValue: `mp-verify-${payload.orgId}-${payload.workspaceId}-${payload.hostname}`,
      verificationToken: `mp-verify-${payload.orgId}-${payload.workspaceId}-${payload.hostname}`,
    };
  }
  return invokeCallable<RecheckCustomDomainCallablePayload, RecheckCustomDomainCallableResult>(
    'recheckCustomDomain',
    payload,
  );
}
